import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Badge, LoadingSpinner, BackButton, ErrorSummary } from '@/components/common';
import { PencilIcon, CubeIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';

/**
 * Product detail -- item 10 of the 2026-09-18 batch.
 *
 * Clicking a product used to open the same `Modal` that editing did; there
 * was no read-only view. This is the new `/admin/products/:id`: Name,
 * Description, "Logo Image" (the closest real field is `thumbnail` -- see the
 * note below), NCB Slabs, Add-Ons, and every mapped Product Plan across every
 * Insurance Company (`ProductDetailSerializer.mapped_plans`, added for this
 * page -- see that serializer's docstring for why it is not read through
 * `InsurancePolicyViewSet`).
 */

interface AssetBrief {
  id: string;
  src: string;
  width: number | null;
  height: number | null;
  alt_text: string;
}

interface MappedPlan {
  id: string;
  insurer_id: string | null;
  insurer_name: string | null;
  name: string;
  code: string;
  is_active: boolean;
}

interface ProductDetail {
  id: string;
  name: string;
  code: string;
  description?: string;
  status: string;
  status_display: string;
  type_name?: string | null;
  group_name?: string | null;
  subgroup_name?: string | null;
  policy_type_name?: string | null;
  add_ons_count: number;
  thumbnail_image?: AssetBrief | null;
  icon_image_asset?: AssetBrief | null;
  mapped_plans: MappedPlan[];
}

interface NcbSlabRow {
  id: string;
  claim_free_years: number;
  ncb_percentage: string;
  vehicle_category: string;
}

interface AddOnRow {
  id: string;
  name: string;
  code: string;
  addon_type_display: string;
  is_percentage_based: boolean;
  percentage_of_premium: string | null;
  fixed_premium: string | null;
  is_mandatory: boolean;
  is_active: boolean;
}

const statusVariant = (status: string): 'success' | 'warning' | 'default' | 'error' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'default';
    case 'discontinued':
      return 'error';
    default:
      return 'warning';
  }
};

const VEHICLE_CATEGORY_LABELS: Record<string, string> = { '2w': 'Two Wheeler', '4w': 'Four Wheeler', cv: 'Commercial Vehicle' };

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useQuery<ProductDetail>({
    queryKey: ['product-detail', id],
    queryFn: async () => (await api.get<ProductDetail>(`/products/products/${id}/`)).data,
    enabled: !!id,
  });

  const {
    data: ncbSlabs = [],
    isLoading: ncbLoading,
    isError: ncbErrorFlag,
    error: ncbError,
  } = useQuery<NcbSlabRow[]>({
    queryKey: ['product-ncb-slabs-view', id],
    queryFn: async () => {
      const res = await api.get('/products/ncb-slabs/', { params: { product: id, page_size: 200 } });
      return res.data.results || res.data;
    },
    enabled: !!id,
  });

  const {
    data: addOns = [],
    isLoading: addOnsLoading,
    isError: addOnsErrorFlag,
    error: addOnsError,
  } = useQuery<AddOnRow[]>({
    queryKey: ['product-add-ons-view', id],
    queryFn: async () => {
      const res = await api.get('/products/add-ons/', { params: { product: id, page_size: 200 } });
      return res.data.results || res.data;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <BackButton to="/admin/products" label="Back to Products" />
        <div className="flex items-center justify-center py-24">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="space-y-6">
        <BackButton to="/admin/products" label="Back to Products" />
        <ErrorSummary messages={[getErrorMessage(error) || 'This product could not be found.']} />
      </div>
    );
  }

  // "Logo Image": the model has no field literally called `logo`/`logo_url`.
  // The closest real fields are `thumbnail` (the card image) and `icon_image`
  // (a small mark) -- this page uses the same thumbnail-then-icon fallback
  // `ProductsListPage`'s own list rows already use as their "logo" avatar.
  const logo = product.thumbnail_image?.src || product.icon_image_asset?.src || null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <BackButton to="/admin/products" label="Back to Products" />
        <Button
          variant="primary"
          leftIcon={<PencilIcon className="w-4 h-4" />}
          onClick={() => navigate(`/admin/products/${id}/edit`)}
        >
          Edit
        </Button>
      </div>

      <Card padding="md">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {logo ? (
            <img
              src={logo}
              alt=""
              width={96}
              height={96}
              className="h-24 w-24 rounded-lg object-cover bg-gray-100 dark:bg-gray-700"
            />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <CubeIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{product.name}</h1>
              <Badge variant={statusVariant(product.status)}>{product.status_display}</Badge>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{product.code}</p>
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Type</dt>
                <dd className="text-gray-900 dark:text-white">{product.type_name || '-'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Group</dt>
                <dd className="text-gray-900 dark:text-white">{product.group_name || '-'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Subgroup</dt>
                <dd className="text-gray-900 dark:text-white">{product.subgroup_name || '-'}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Policy Type</dt>
                <dd className="text-gray-900 dark:text-white">{product.policy_type_name || '-'}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Description</h3>
          <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
            {product.description || 'No description.'}
          </p>
        </div>
      </Card>

      <Card padding="md">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">NCB Slabs</h3>
        {ncbLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : ncbErrorFlag ? (
          <ErrorSummary messages={[getErrorMessage(ncbError)]} />
        ) : ncbSlabs.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">This product has no NCB slabs configured.</p>
        ) : (
          <table className="mt-3 min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4">Claim-free Years</th>
                <th className="py-2 pr-4">NCB %</th>
                <th className="py-2 pr-4">Vehicle Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {ncbSlabs.map((slab) => (
                <tr key={slab.id}>
                  <td className="py-2 pr-4 text-gray-900 dark:text-white">{slab.claim_free_years}</td>
                  <td className="py-2 pr-4 text-gray-900 dark:text-white">{slab.ncb_percentage}%</td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {VEHICLE_CATEGORY_LABELS[slab.vehicle_category] || slab.vehicle_category || 'All'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card padding="md">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Add-Ons {product.add_ons_count ? `(${product.add_ons_count} active)` : ''}
        </h3>
        {addOnsLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : addOnsErrorFlag ? (
          <ErrorSummary messages={[getErrorMessage(addOnsError)]} />
        ) : addOns.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">This product has no add-ons configured.</p>
        ) : (
          <table className="mt-3 min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Pricing</th>
                <th className="py-2 pr-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {addOns.map((addOn) => (
                <tr key={addOn.id}>
                  <td className="py-2 pr-4 text-gray-900 dark:text-white">{addOn.name}</td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{addOn.addon_type_display}</td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {addOn.is_percentage_based
                      ? `${addOn.percentage_of_premium ?? '-'}% of premium`
                      : `Flat ${addOn.fixed_premium ?? '-'}`}
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant={addOn.is_active ? 'success' : 'warning'}>
                      {addOn.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card padding="md">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Mapped Product Plans</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Every plan an Insurance Company has mapped to this catalog product.
        </p>
        {product.mapped_plans.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No insurer has mapped a plan to this product yet.
          </p>
        ) : (
          <table className="mt-3 min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4">Insurance Company</th>
                <th className="py-2 pr-4">Plan Name</th>
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {product.mapped_plans.map((plan) => (
                <tr key={plan.id}>
                  <td className="py-2 pr-4 text-gray-900 dark:text-white">{plan.insurer_name || '-'}</td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{plan.name}</td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{plan.code}</td>
                  <td className="py-2 pr-4">
                    <Badge variant={plan.is_active ? 'success' : 'warning'}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default ProductDetailPage;
